import { useId, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

export interface InheritanceSectionProps {
  /** The section's title — what the switch overrides. */
  readonly title: string;
  /** True when this level sets the section; false when it inherits it. */
  readonly overridden: boolean;
  /**
   * Called with the new state. Turning the override ON is expected to start from the inherited
   * value, so the operator edits a value instead of re-typing it; OFF drops the section.
   * Absent: the section cannot be overridden at this level (shown with `lockedLabel`).
   */
  readonly onOverriddenChange?: ((overridden: boolean) => void) | undefined;
  readonly readOnly?: boolean;
  /** "geerbt" / "überschrieben" — the two states, in the caller's words. */
  readonly inheritedLabel: string;
  readonly overriddenLabel: string;
  /** The accessible name of the switch, e.g. "Wartezeit: überschreiben". */
  readonly switchLabel: string;
  /** Shown beside an inherited section: "Vom Tarif übernommen". */
  readonly inheritedBadge: string;
  /** Shown instead of the switch when the section can never be overridden: "Nur im Tarif änderbar". */
  readonly lockedLabel?: string;
  /** The inherited value, rendered read-only — shown while inherited. */
  readonly inherited: ReactNode;
  /** The editor for this level's own value — shown while overridden. */
  readonly children?: ReactNode;
}

/**
 * One section of a document that a lower level may OVERRIDE and otherwise INHERITS at read
 * (SDD-007): a group tarif's `billing_config` knob, a group tarif's `tarif_deckung` section
 * (SDD-018 §6, ui-spec §5). "Geerbt" shows what is inherited today, read-only and badged;
 * "überschrieben" shows this level's editor. The switch is the only way between the two, so an
 * operator can always see which one they are looking at.
 *
 * Presentational: which value is inherited and how an override starts are the caller's — a pattern
 * that knew the document would be a second copy of the resolver.
 */
export function InheritanceSection({
  title,
  overridden,
  onOverriddenChange,
  readOnly = false,
  inheritedLabel,
  overriddenLabel,
  switchLabel,
  inheritedBadge,
  lockedLabel,
  inherited,
  children,
}: InheritanceSectionProps): ReactNode {
  const titleId = useId();
  const locked = onOverriddenChange === undefined;
  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        border: 1,
        borderColor: overridden ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography id={titleId} variant="subtitle2" component="h3">
            {title}
          </Typography>
          <Chip
            size="small"
            variant={overridden ? 'filled' : 'outlined'}
            color={overridden ? 'primary' : 'default'}
            label={overridden ? overriddenLabel : inheritedLabel}
          />
        </Stack>
        {locked ? (
          lockedLabel !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {lockedLabel}
            </Typography>
          )
        ) : (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={overridden}
                disabled={readOnly}
                onChange={(event) => {
                  onOverriddenChange(event.target.checked);
                }}
                slotProps={{ input: { 'aria-label': switchLabel } }}
              />
            }
            label={overridden ? overriddenLabel : inheritedLabel}
          />
        )}
      </Stack>
      {overridden ? (
        children
      ) : (
        <Box>
          <Chip size="small" variant="outlined" label={inheritedBadge} sx={{ mb: 1 }} />
          {inherited}
        </Box>
      )}
    </Box>
  );
}
